import { useSessionStore } from '../Stores/Session';

export const protectedFileId = (value: string): string | null => {
  try {
    const u = new URL(value);
    const trusted = u.protocol === 'https:' && ['files.gleap.io', 'files.us.gleap.ai'].includes(u.hostname) && !u.port;
    const local = u.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(u.hostname);
    if ((!trusted && !local) || u.username || u.password) return null;
    return u.pathname.match(/^(?:\/files)?\/af\/([a-f0-9]{24})$/)?.[1] ?? null;
  } catch { return null; }
};
export const protectedFileHeaders = (url: string): Record<string, string> => {
  if (!protectedFileId(url)) return {};
  const { apiUrl, session } = useSessionStore.getState();
  const token = session?.fileAccessToken;
  if (!session?.fileAccessExpiresAt || Date.parse(session.fileAccessExpiresAt) <= Date.now()) return {};
  const api = new URL(apiUrl || 'https://dashapi.gleap.io');
  const file = new URL(url);
  const expected = api.hostname.endsWith('.us.gleap.ai') ? 'files.us.gleap.ai' : 'files.gleap.io';
  if (file.hostname !== expected && !(file.origin === api.origin && ['localhost', '127.0.0.1'].includes(api.hostname))) return {};
  return token ? { 'X-File-Session': token } : {};
};
export const fetchProtectedFile = async (url: string) => {
  const response = await fetch(url, { headers: protectedFileHeaders(url), cache: 'no-store', credentials: 'omit', redirect: 'error' });
  if (!response.ok) throw new Error('Sign in with permission to view this file.');
  return response;
};

/** Open protected files within this iframe; never hand credentials to the host. */
export const openProtectedFile = async (url: string) => {
  const dialog = document.createElement('dialog');
  const close = document.createElement('button'); close.textContent = 'Close';
  const status = document.createElement('p'); status.textContent = 'Loading file…';
  dialog.append(close, status); document.body.append(dialog);
  let objectUrl: string | undefined;
  let closed = false;
  const identity = useSessionStore.getState().session?.gleapId;
  const cleanup = () => { closed = true; if (objectUrl) URL.revokeObjectURL(objectUrl); unsubscribe(); dialog.remove(); };
  const unsubscribe = useSessionStore.subscribe((state) => { if (state.session?.gleapId !== identity || !state.session?.fileAccessToken) cleanup(); });
  close.onclick = cleanup; dialog.oncancel = cleanup; dialog.showModal();
  try {
    const blob = await (await fetchProtectedFile(url)).blob();
    if (closed) return;
    objectUrl = URL.createObjectURL(blob);
    if (blob.type.startsWith('image/') && !blob.type.includes('svg')) {
      const image = document.createElement('img'); image.src = objectUrl; image.style.maxWidth = '100%'; dialog.append(image);
    }
    const download = document.createElement('a'); download.href = objectUrl; download.download = 'attachment'; download.textContent = 'Download file';
    dialog.append(download); status.remove();
  } catch { status.textContent = 'Sign in with permission to view this file.'; }
};
export const initializeAuthenticatedFiles = async () => {
  // SDK bootstrap can inherit the CUSTOMER origin via document.write. Never
  // register a service worker there: that would affect the customer's site.
  document.addEventListener('click', (event) => {
    const element = event.target as Element;
    const anchor = element?.closest?.('a');
    const url = (element?.tagName === 'IMG' && element.getAttribute('data-protected-file')) || anchor?.href;
    if (!url || !protectedFileId(url)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void openProtectedFile(url);
  }, true);
};
